import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Button, Chip, IconButton, Dialog, DialogTitle,
    DialogContent, DialogActions, CircularProgress, Alert, Card, CardContent,
    Grid, Avatar, Tooltip, useTheme, useMediaQuery, TablePagination,
    FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import {
    Store, Person, Visibility, CheckCircle, TrendingUp, Refresh,
    Phone, Email, Business, AttachMoney, Inventory, ShoppingCart,
    FilterList, Star, LocalShipping
} from '@mui/icons-material';
import adminService from '../services/adminService';

// Base URL for serving uploaded static files
const ASSET_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || 'https://xaintro-backend.onrender.com';

const VerifiedSellers = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [loading, setLoading] = useState(true);
    const [sellers, setSellers] = useState([]);
    const [filteredSellers, setFilteredSellers] = useState([]);
    const [error, setError] = useState(null);
    const [selectedSeller, setSelectedSeller] = useState(null);
    const [detailsDialog, setDetailsDialog] = useState(false);
    const [sortBy, setSortBy] = useState('revenue'); // 'revenue', 'orders', 'products', 'newest'

    // Pagination state
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const loadVerifiedSellers = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await adminService.getSellers();
            const verifiedSellers = response.sellers.filter(seller => seller.sellerStatus === 'verified');
            setSellers(verifiedSellers);
            setFilteredSellers(verifiedSellers);
        } catch (err) {
            console.error('Failed to load verified sellers:', err);
            setError('Failed to load verified sellers');
        } finally {
            setLoading(false);
        }
    }, []);

    // Sort sellers based on selected criteria
    useEffect(() => {
        let sorted = [...sellers];
        
        switch (sortBy) {
            case 'revenue':
                sorted.sort((a, b) => (b.stats?.totalRevenue || 0) - (a.stats?.totalRevenue || 0));
                break;
            case 'orders':
                sorted.sort((a, b) => (b.stats?.completedOrders || 0) - (a.stats?.completedOrders || 0));
                break;
            case 'products':
                sorted.sort((a, b) => (b.stats?.totalProducts || 0) - (a.stats?.totalProducts || 0));
                break;
            case 'newest':
                sorted.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
                break;
            default:
                break;
        }
        
        setFilteredSellers(sorted);
    }, [sellers, sortBy]);

    useEffect(() => {
        loadVerifiedSellers();
    }, [loadVerifiedSellers]);

    const handleViewDetails = (seller) => {
        setSelectedSeller(seller);
        setDetailsDialog(true);
    };

    const getPerformanceColor = (value, type) => {
        if (type === 'revenue') {
            if (value >= 100000) return 'success';
            if (value >= 50000) return 'warning';
            return 'default';
        }
        if (type === 'orders') {
            if (value >= 100) return 'success';
            if (value >= 50) return 'warning';
            return 'default';
        }
        if (type === 'products') {
            if (value >= 50) return 'success';
            if (value >= 20) return 'warning';
            return 'default';
        }
        return 'default';
    };

    const getTopPerformers = () => {
        const topRevenue = [...filteredSellers].sort((a, b) => (b.stats?.totalRevenue || 0) - (a.stats?.totalRevenue || 0)).slice(0, 3);
        const topOrders = [...filteredSellers].sort((a, b) => (b.stats?.completedOrders || 0) - (a.stats?.completedOrders || 0)).slice(0, 3);
        const topProducts = [...filteredSellers].sort((a, b) => (b.stats?.totalProducts || 0) - (a.stats?.totalProducts || 0)).slice(0, 3);
        
        return { topRevenue, topOrders, topProducts };
    };

    const { topRevenue, topOrders, topProducts } = getTopPerformers();

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <CheckCircle sx={{ fontSize: 32, color: '#4CAF50' }} />
                    <Box>
                        <Typography variant="h5" fontWeight={700}>
                            Verified Sellers
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Active and verified sellers on the platform
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Chip label={`${filteredSellers.length} Verified`} color="success" sx={{ fontWeight: 600 }} />
                    <IconButton onClick={loadVerifiedSellers}>
                        <Refresh />
                    </IconButton>
                </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

            {/* Stats Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={3}>
                    <Card sx={{ bgcolor: '#e8f5e9' }}>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <CheckCircle sx={{ fontSize: 40, color: '#4CAF50' }} />
                            <Typography variant="h4" fontWeight={700}>{filteredSellers.length}</Typography>
                            <Typography variant="body2" color="text.secondary">Verified Sellers</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Card sx={{ bgcolor: '#fce4ec' }}>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <AttachMoney sx={{ fontSize: 40, color: '#E91E63' }} />
                            <Typography variant="h4" fontWeight={700}>
                                {filteredSellers.reduce((sum, s) => sum + (s.stats?.totalRevenue || 0), 0).toLocaleString()}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">Total Revenue (PKR)</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Card sx={{ bgcolor: '#e3f2fd' }}>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <ShoppingCart sx={{ fontSize: 40, color: '#2196F3' }} />
                            <Typography variant="h4" fontWeight={700}>
                                {filteredSellers.reduce((sum, s) => sum + (s.stats?.completedOrders || 0), 0)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">Total Orders</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Card sx={{ bgcolor: '#fff3e0' }}>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <Inventory sx={{ fontSize: 40, color: '#FF9800' }} />
                            <Typography variant="h4" fontWeight={700}>
                                {filteredSellers.reduce((sum, s) => sum + (s.stats?.totalProducts || 0), 0)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">Total Products</Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Top Performers */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 2, bgcolor: '#fafafa' }}>
                        <Typography variant="subtitle2" fontWeight={600} color="text.secondary" gutterBottom>
                            <TrendingUp sx={{ fontSize: 16, verticalAlign: 'middle', mr: 1 }} />
                            Top Revenue
                        </Typography>
                        {topRevenue.map((seller, index) => (
                            <Box key={seller._id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Chip 
                                    label={index + 1} 
                                    size="small" 
                                    color="default"
                                    sx={{ bgcolor: index === 0 ? '#FFD700' : index === 1 ? '#C0C0C0' : '#CD7F32', color: 'white' }}
                                />
                                <Typography variant="caption" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {seller.brandName || seller.name}
                                </Typography>
                                <Typography variant="caption" fontWeight={600} color="success.main">
                                    PKR {(seller.stats?.totalRevenue || 0).toLocaleString()}
                                </Typography>
                            </Box>
                        ))}
                    </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 2, bgcolor: '#fafafa' }}>
                        <Typography variant="subtitle2" fontWeight={600} color="text.secondary" gutterBottom>
                            <ShoppingCart sx={{ fontSize: 16, verticalAlign: 'middle', mr: 1 }} />
                            Top Orders
                        </Typography>
                        {topOrders.map((seller, index) => (
                            <Box key={seller._id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Chip 
                                    label={index + 1} 
                                    size="small" 
                                    sx={{ bgcolor: index === 0 ? '#4CAF50' : index === 1 ? '#8BC34A' : '#CDDC39', color: 'white' }}
                                />
                                <Typography variant="caption" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {seller.brandName || seller.name}
                                </Typography>
                                <Typography variant="caption" fontWeight={600} color="primary.main">
                                    {seller.stats?.completedOrders || 0} orders
                                </Typography>
                            </Box>
                        ))}
                    </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 2, bgcolor: '#fafafa' }}>
                        <Typography variant="subtitle2" fontWeight={600} color="text.secondary" gutterBottom>
                            <Inventory sx={{ fontSize: 16, verticalAlign: 'middle', mr: 1 }} />
                            Top Products
                        </Typography>
                        {topProducts.map((seller, index) => (
                            <Box key={seller._id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Chip 
                                    label={index + 1} 
                                    size="small" 
                                    sx={{ bgcolor: index === 0 ? '#FF9800' : index === 1 ? '#FFC107' : '#FFEB3B', color: index === 2 ? 'black' : 'white' }}
                                />
                                <Typography variant="caption" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {seller.brandName || seller.name}
                                </Typography>
                                <Typography variant="caption" fontWeight={600} color="warning.main">
                                    {seller.stats?.totalProducts || 0} products
                                </Typography>
                            </Box>
                        ))}
                    </Paper>
                </Grid>
            </Grid>

            {/* Filter Section */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FilterList color="action" />
                        <Typography variant="subtitle2" fontWeight={600}>Sort By:</Typography>
                    </Box>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Sort</InputLabel>
                        <Select
                            value={sortBy}
                            label="Sort"
                            onChange={(e) => setSortBy(e.target.value)}
                        >
                            <MenuItem value="revenue">Highest Revenue</MenuItem>
                            <MenuItem value="orders">Most Orders</MenuItem>
                            <MenuItem value="products">Most Products</MenuItem>
                            <MenuItem value="newest">Newest First</MenuItem>
                        </Select>
                    </FormControl>
                    <Chip
                        label={`Showing ${filteredSellers.length} sellers`}
                        size="small"
                        color="primary"
                        variant="outlined"
                    />
                </Box>
            </Paper>

            {/* Verified Sellers Table */}
            {filteredSellers.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <Store sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">
                        No verified sellers found
                    </Typography>
                </Paper>
            ) : (
                <TableContainer 
                    component={Paper}
                    sx={{ 
                        maxHeight: { xs: '400px', sm: 'none' },
                        overflow: { xs: 'auto', sm: 'visible' }
                    }}
                >
                    <Table size={isMobile ? "small" : "medium"}>
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                <TableCell sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>Seller</TableCell>
                                <TableCell sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem', display: { xs: 'none', sm: 'table-cell' } }}>Performance</TableCell>
                                <TableCell align="center" sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem', display: { xs: 'none', sm: 'table-cell' } }}>Products</TableCell>
                                <TableCell align="center" sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem', display: { xs: 'none', sm: 'table-cell' } }}>Orders</TableCell>
                                <TableCell align="center" sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem', display: { xs: 'none', sm: 'table-cell' } }}>Revenue</TableCell>
                                <TableCell align="right" sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredSellers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((seller) => (
                                <TableRow key={seller._id} hover>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Avatar
                                                src={(seller.profileImage || seller.shopImage) ? 
                                                    (seller.profileImage || seller.shopImage).startsWith('http') ? 
                                                        (seller.profileImage || seller.shopImage) : 
                                                        `${ASSET_BASE}${seller.profileImage || seller.shopImage}` : undefined}
                                                sx={{ bgcolor: '#4CAF50', width: isMobile ? 40 : 48, height: isMobile ? 40 : 48 }}
                                            >
                                                <Store sx={{ fontSize: isMobile ? 16 : 20 }} />
                                            </Avatar>
                                            <Box>
                                                <Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}>
                                                    {seller.brandName || seller.name}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem' }}>
                                                    {seller.email}
                                                </Typography>
                                                {/* Mobile performance display */}
                                                <Box sx={{ display: { xs: 'flex', sm: 'none' }, alignItems: 'center', gap: 1, mt: 0.5 }}>
                                                    <Chip
                                                        label={`PKR ${(seller.stats?.totalRevenue || 0).toLocaleString()}`}
                                                        color={getPerformanceColor(seller.stats?.totalRevenue || 0, 'revenue')}
                                                        size="small"
                                                        sx={{ fontSize: '0.6rem' }}
                                                    />
                                                    <Typography variant="caption" color="text.secondary">
                                                        {seller.stats?.completedOrders || 0} orders
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Star sx={{ fontSize: 16, color: '#FFD700' }} />
                                            <Typography variant="body2" fontWeight={600}>
                                                Top Performer
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell align="center" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                                        <Chip
                                            label={seller.stats?.totalProducts || 0}
                                            color={getPerformanceColor(seller.stats?.totalProducts || 0, 'products')}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell align="center" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                                        <Chip
                                            label={seller.stats?.completedOrders || 0}
                                            color={getPerformanceColor(seller.stats?.completedOrders || 0, 'orders')}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell align="center" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                                        <Typography fontWeight={600} color="success.main">
                                            PKR {(seller.stats?.totalRevenue || 0).toLocaleString()}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Tooltip title="View Details">
                                            <IconButton
                                                onClick={() => handleViewDetails(seller)}
                                                color="primary"
                                                size={isMobile ? "small" : "medium"}
                                            >
                                                <Visibility sx={{ fontSize: isMobile ? 16 : 20 }} />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Pagination */}
            {filteredSellers.length > rowsPerPage && (
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    component="div"
                    count={filteredSellers.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(_, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                />
            )}

            {/* Seller Details Dialog */}
            <Dialog 
                open={detailsDialog} 
                onClose={() => setDetailsDialog(false)} 
                maxWidth="md" 
                fullWidth
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: '#4CAF50', width: 48, height: 48 }}>
                            <Store />
                        </Avatar>
                        <Box>
                            <Typography variant="h6" fontWeight={700}>
                                {selectedSeller?.brandName || selectedSeller?.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {selectedSeller?.email}
                            </Typography>
                            <Chip
                                label="Verified Seller"
                                color="success"
                                size="small"
                                sx={{ mt: 0.5 }}
                            />
                        </Box>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {selectedSeller && (
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                    Total Revenue
                                </Typography>
                                <Typography variant="h6" fontWeight={700} color="success.main">
                                    PKR {(selectedSeller.stats?.totalRevenue || 0).toLocaleString()}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                    Completed Orders
                                </Typography>
                                <Typography variant="h6" fontWeight={700} color="primary.main">
                                    {selectedSeller.stats?.completedOrders || 0}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                    Total Products
                                </Typography>
                                <Typography variant="h6" fontWeight={700} color="warning.main">
                                    {selectedSeller.stats?.totalProducts || 0}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                    Member Since
                                </Typography>
                                <Typography variant="body1">
                                    {selectedSeller.createdAt ? new Date(selectedSeller.createdAt).toLocaleDateString() : 'N/A'}
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                    Business Description
                                </Typography>
                                <Typography variant="body1">
                                    {selectedSeller.storeDescription || selectedSeller.businessDescription || 'No description provided'}
                                </Typography>
                            </Grid>
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDetailsDialog(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default VerifiedSellers;
